import type { PostHog } from 'posthog-js';
import { setToken } from '@/Config/auth/authStore';
import { trackAndNavigate } from '@/Config/libs/analytics';
import type { LoginResponse } from '@/Domain/api/User/dto/User.dto';
import { LoginMethodEnum } from '@/Domain/enum/LoginMethod.enum';
import { PostHogEventEnum, PostHogFeatureFlagEnum } from '@/Domain/enum/PostHogEvent.enum';
import { rememberLoginMethod } from '@/Domain/utils/lastLoginMethod';
import { clearPendingVerificationEmail } from '@/Domain/utils/pendingVerificationEmail';
import { readNextParamFromSearch } from '@/Domain/utils/safeNextPath';

export type LoginMethod = 'password' | 'passkey';

const TRACK_EVENT_BY_METHOD: Record<LoginMethod, string> = {
  password: 'login_form_success',
  passkey: 'login_passkey_success',
};

const LAST_USED_BY_METHOD: Record<LoginMethod, LoginMethodEnum> = {
  password: LoginMethodEnum.Email,
  passkey: LoginMethodEnum.Passkey,
};

export const completeLogin = (
  response: LoginResponse,
  postHog: PostHog | undefined,
  method: LoginMethod,
): void => {
  clearPendingVerificationEmail();
  rememberLoginMethod(LAST_USED_BY_METHOD[method]);

  // Store JWT from login response in auth store
  if (response.jwt_token) {
    setToken(response.jwt_token);
  }

  // Identify user in PostHog so this event and future events are linked to
  // the user. Keyed on the stable numeric user_id to preserve continuity
  // with anonymous→identified history captured pre-SPA migration.
  postHog?.identify(response.user_id.toString());

  // Get the A/B test variant for tracking
  const backgroundVariant = postHog?.getFeatureFlag(PostHogFeatureFlagEnum.SignupBackgroundTest);

  const scenarioId = response.default_scenario_id;
  const nextPresent = new URLSearchParams(window.location.search).get('next') !== null;
  // The logged-out MCP-authorize redirect lands here as a URL `next`; replay it.
  const nextPath = readNextParamFromSearch(window.location.search);
  const navigateTo = nextPath ?? (scenarioId ? `/i/${scenarioId}/` : '/');

  // Experiment analysis + MCP logged-out-resume visibility. next_present=URL had `next`;
  // next_replayed=we navigated to it (present && !replayed = rejected by getSafeNextPath).
  postHog?.capture(PostHogEventEnum.LoginCompleted, {
    background_variant: backgroundVariant,
    next_present: nextPresent,
    next_replayed: nextPath !== null,
    method,
  });

  trackAndNavigate(
    TRACK_EVENT_BY_METHOD[method],
    {
      event_category: 'User login',
      event_label: method === 'password' ? 'login_form' : 'login_passkey',
      background_variant: backgroundVariant ?? 'unknown',
    },
    () => {
      // Full document navigation: the entry shell (login route) is a
      // separate router tree from the operator-app routes, so we always
      // reload to remount the SPA root with the post-login state.
      window.location.href = navigateTo;
    },
  );
};
